<script setup lang="ts">
// Accessible multi-select button group. Renders real <input type="checkbox">
// elements styled to look like the deprecated ButtonGroup buttons, so screen
// readers announce each option as a checkbox and its checked state. Always
// multi-select — the <fieldset>/<legend> pair is rendered here so the group
// always has an accessible name.
import { computed, ref, useId } from 'vue';

import { KaDescription, KaMessage } from '@/kasten-components/components';
import type { KaVeeFormFieldWithoutInputEmitContract } from '@/kasten-components/components/KaVeeFormField';
import { unwrapElement } from '@/kasten-components/utils';

import type {
  KaCheckboxButtonGroupOption,
  KaCheckboxButtonGroupProps,
  KaCheckboxButtonGroupValue,
} from './types';

const props = withDefaults(defineProps<KaCheckboxButtonGroupProps>(), {
  modelValue: () => [],
  hideLabel: false,
  description: undefined,
  errorMessage: undefined,
  size: 'normal',
  width: '',
  disabled: false,
  symmetric: false,
  grid: false,
  columns: 0,
});

// touch/change/blur satisfy KaVeeFormFieldWithoutInputEmitContract so the
// component can be wrapped by KaVeeFormField (see KaCheckboxButtonGroupVee).
const emit = defineEmits<
  KaVeeFormFieldWithoutInputEmitContract<KaCheckboxButtonGroupValue> & {
    (e: 'update:modelValue', value: KaCheckboxButtonGroupValue): void;
  }
>();

const fieldSetRef = ref<HTMLFieldSetElement>();

const compact = computed(() => props.size === 'compact');
const isGridMode = computed(() => props.grid || props.columns > 0);

const uid = useId();
// Treat an empty string as no error so the group is never aria-invalid without a visible message
const hasError = computed(() => !!props.errorMessage);
const descriptionId = computed(() => (props.description ? `${uid}-description` : undefined));
const errorMessageId = computed(() => (hasError.value ? `${uid}-error-message` : undefined));
// Combine description and error ids so SRs announce both when entering the group
const describedByIds = computed(() => {
  const ids = [descriptionId.value, errorMessageId.value].filter(Boolean);
  return ids.length ? ids.join(' ') : undefined;
});

function optionClasses(item: KaCheckboxButtonGroupOption) {
  const selected = isSelected(item);
  return [
    'k-button-group-option btn btn-secondary group relative flex flex-[2_1_auto] cursor-pointer items-center justify-center whitespace-nowrap font-normal transition-all duration-100 hover:bg-primary hover:text-white focus-within:outline-none focus-within:border-primary dark:focus-within:border-blue-400',
    selected ? 'bg-primary text-white' : '',
    props.disabled || item.disabled ? 'cursor-not-allowed opacity-65' : '',
    props.symmetric ? 'min-[711px]:grow-0' : '',
    compact.value
      ? 'mx-[0.1rem] mb-[0.4rem] px-[0.4rem] py-[0.185rem] text-[0.85rem]'
      : props.size === 'small'
        ? 'mb-0 text-[0.85rem]'
        : 'mb-0',
  ];
}

const textContainerClasses = computed(() => (compact.value ? '' : 'pl-[0.4rem]'));

function isSelected(item: KaCheckboxButtonGroupOption) {
  return props.modelValue.includes(item.value);
}

function toggle(value: string | number) {
  const next = props.modelValue.includes(value)
    ? props.modelValue.filter((v) => v !== value)
    : [...props.modelValue, value];
  emit('touch');
  emit('update:modelValue', next);
  emit('change', next);
}

// Defer so relatedTarget/activeElement settles, then only emit blur when focus
// actually left the group — moving between option checkboxes is not a blur.
const handleBlur = (event: FocusEvent) =>
  setTimeout(() => {
    if (shouldDispatchBlur(event)) {
      emit('blur', event);
    }
  });

const shouldDispatchBlur = (event: FocusEvent) => {
  const nextTarget = event.relatedTarget ?? document.activeElement;
  if (nextTarget === null) return true;
  if (!(nextTarget instanceof Node)) return true;

  const fieldsetEl = unwrapElement(fieldSetRef);
  if (!fieldsetEl) return true;

  return !fieldsetEl.contains(nextTarget);
};
</script>

<template>
  <fieldset ref="fieldSetRef" :aria-describedby="describedByIds">
    <!-- Keep <fieldset> as the template's only root node (no sibling comments):
    a root-level comment makes this a fragment in dev builds, which breaks
    consumers that wrap the group in <transition>. -->
    <!-- aria-invalid/aria-errormessage live on the checkbox inputs, not on the <fieldset>:
    ARIA does not support them on role "group", so tooling ignores them there. -->
    <legend
      class="text-sm font-semibold leading-6 text-gray-900 dark:text-gray-50"
      :class="{ 'sr-only': props.hideLabel }"
    >
      {{ props.label }}
    </legend>
    <KaDescription v-if="props.description" :id="descriptionId" :description="props.description" />
    <!-- The default slot replaces only the option buttons (e.g. with a message explaining why
    they are unavailable) while the legend, description and error message stay visible. -->
    <div v-if="$slots.default" class="pb-[0.2rem] pt-2">
      <slot />
    </div>
    <div
      v-else
      class="relative pb-[0.2rem] pt-2"
      :class="[
        compact ? 'gap-0' : 'gap-2.5',
        isGridMode
          ? 'grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))]'
          : 'flex flex-wrap justify-start',
        props.symmetric ? 'min-[711px]:justify-between' : '',
      ]"
      :style="props.columns ? `grid-template-columns: repeat(${props.columns}, 1fr)` : ''"
    >
      <label
        v-for="item in props.options"
        :key="item.value"
        :class="optionClasses(item)"
        :style="!isGridMode && props.width ? `width: ${props.width}` : ''"
        :data-testid="`k-button-group-option-${item.value}`"
      >
        <!-- Stop the native `input` event: KaVeeFormField's onInput lands on the fieldset as a
        fallthrough attr (`input` is not a declared emit) and would overwrite the array field
        value with this single checkbox's value. Only the emitted `change` carries the array. -->
        <input
          type="checkbox"
          class="sr-only"
          :name="props.name"
          :value="item.value"
          :checked="isSelected(item)"
          :disabled="props.disabled || item.disabled"
          :aria-invalid="hasError || undefined"
          :aria-errormessage="errorMessageId"
          @change="toggle(item.value)"
          @input.stop
          @blur="handleBlur"
        />
        <span :class="textContainerClasses">
          <span>{{ item.label }}</span>
          <span
            v-if="item.details"
            class="whitespace-normal text-[0.8rem] text-gray-600 group-hover:text-white"
            :class="{ 'text-white': isSelected(item) }"
            >{{ item.details }}</span
          >
        </span>
      </label>
    </div>
    <KaMessage
      v-if="hasError"
      :id="errorMessageId"
      :message="props.errorMessage"
      background="transparent"
      variant="error"
      :role="null"
      class="mt-1"
    />
  </fieldset>
</template>
